'use client';

import { md5 } from '@noble/hashes/legacy.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex } from '@noble/hashes/utils.js';

import {
  type BrowserUploadControl,
  fetchBrowserUploadControl,
} from './browser-upload-session-client';
import { uploadProxyRequestUrl } from './upload-via-proxy';

/**
 * Wire pieces of the browser multipart upload (browser-multipart-upload.ts):
 * the part layout, one streaming hash pass, the session control POST and
 * one part PUT through the upload proxy with progress.
 */

export type BrowserUploadErrorCode =
  | 'WORKSPACE_STORAGE_LIMIT_EXCEEDED'
  /** The stored bytes did not verify; the session is quarantined, never retried. */
  | 'UPLOAD_CONTENT_UNVERIFIED'
  | 'UPLOAD_ABORTED'
  | 'UPLOAD_PART_FAILED'
  | 'UPLOAD_SESSION_FAILED';

export class BrowserUploadError extends Error {
  readonly code: BrowserUploadErrorCode;
  /** The HTTP status the part PUT or the session route answered with, if any. */
  readonly httpStatus: number | undefined;
  /** The request got no HTTP answer at all (a reset, a block, no connection). */
  readonly noResponse: boolean;
  constructor(
    code: BrowserUploadErrorCode,
    message?: string,
    detail: { httpStatus?: number; noResponse?: boolean } = {}
  ) {
    super(message ? `${code}: ${message}` : code);
    this.name = 'BrowserUploadError';
    this.code = code;
    this.httpStatus = detail.httpStatus;
    this.noResponse = detail.noResponse === true;
  }
}

/** Parts of `partBytes` (the last one shorter); an empty body is one empty part. */
export function browserUploadPartCount(
  sizeBytes: number,
  partBytes: number
): number {
  return Math.max(1, Math.ceil(sizeBytes / partBytes));
}

/** Byte range [start, end) of a 1-based part. */
export function browserUploadPartRange(
  sizeBytes: number,
  partBytes: number,
  partNumber: number
): { start: number; end: number } {
  const start = (partNumber - 1) * partBytes;
  return { start, end: Math.min(sizeBytes, start + partBytes) };
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/** sha256 of the whole Blob and the base64 MD5 of each part, in one streaming pass. */
export async function hashBrowserUploadParts(
  blob: Blob,
  partBytes: number,
  signal?: AbortSignal
): Promise<{ contentHash: string; partMd5s: string[]; sizeBytes: number }> {
  const whole = sha256.create();
  const partMd5s: string[] = [];
  let part = md5.create();
  let partFill = 0;
  const reader = blob.stream().getReader();
  for (;;) {
    if (signal?.aborted) {
      await reader.cancel().catch(() => undefined);
      throw new BrowserUploadError('UPLOAD_ABORTED');
    }
    const { value, done } = await reader.read();
    if (done) break;
    whole.update(value);
    let offset = 0;
    while (offset < value.length) {
      const take = Math.min(partBytes - partFill, value.length - offset);
      part.update(value.subarray(offset, offset + take));
      partFill += take;
      offset += take;
      if (partFill === partBytes) {
        partMd5s.push(base64(part.digest()));
        part = md5.create();
        partFill = 0;
      }
    }
  }
  if (partFill > 0 || partMd5s.length === 0)
    partMd5s.push(base64(part.digest()));
  return {
    contentHash: `sha256:${bytesToHex(whole.digest())}`,
    partMd5s,
    sizeBytes: blob.size,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** POST one session operation; a refusal the caller acts on keeps its code. */
export async function postBrowserUploadOperation(
  control: BrowserUploadControl,
  body: Record<string, unknown>,
  signal?: AbortSignal
): Promise<Record<string, unknown>> {
  let response: Response;
  try {
    response = await fetchBrowserUploadControl(control, body, signal);
  } catch {
    if (signal?.aborted) throw new BrowserUploadError('UPLOAD_ABORTED');
    throw new BrowserUploadError(
      'UPLOAD_SESSION_FAILED',
      String(body.operation),
      { noResponse: true }
    );
  }
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error =
      isRecord(payload) && typeof payload.error === 'string'
        ? payload.error
        : '';
    if (error === 'WORKSPACE_STORAGE_LIMIT_EXCEEDED') {
      throw new BrowserUploadError(
        'WORKSPACE_STORAGE_LIMIT_EXCEEDED',
        undefined,
        {
          httpStatus: response.status,
        }
      );
    }
    if (error === 'BROWSER_UPLOAD_CONTENT_MISMATCH') {
      throw new BrowserUploadError('UPLOAD_CONTENT_UNVERIFIED', undefined, {
        httpStatus: response.status,
      });
    }
    throw new BrowserUploadError(
      'UPLOAD_SESSION_FAILED',
      `${String(body.operation)} ${response.status}${error ? ` ${error}` : ''}`,
      { httpStatus: response.status }
    );
  }
  if (!isRecord(payload)) {
    throw new BrowserUploadError(
      'UPLOAD_SESSION_FAILED',
      `${String(body.operation)} reply`,
      { httpStatus: response.status }
    );
  }
  return payload;
}

/** The presigned PUT URL a session reply carries. */
export function uploadUrlOf(reply: Record<string, unknown>): string {
  if (typeof reply.uploadUrl !== 'string' || !reply.uploadUrl) {
    throw new BrowserUploadError('UPLOAD_SESSION_FAILED', 'missing part URL');
  }
  return reply.uploadUrl;
}

function putTargetUrl(signedUrl: string): string {
  // Same routing as upload-via-proxy.ts: local dev goes through the guarded
  // same-origin route, deployments through the upload proxy worker.
  const proxyUrl =
    process.env.NODE_ENV === 'development'
      ? '/api/upload-proxy'
      : process.env.NEXT_PUBLIC_UPLOAD_PROXY_URL;
  return proxyUrl ? uploadProxyRequestUrl(proxyUrl, signedUrl) : signedUrl;
}

/**
 * PUT one part with its signed Content-MD5. Resolves the part's ETag (may be
 * null when the proxy hides it); rejects with UPLOAD_PART_FAILED or
 * UPLOAD_ABORTED.
 */
export function putBrowserUploadPart(args: {
  signedUrl: string;
  body: Blob;
  contentType: string;
  contentMd5: string;
  signal?: AbortSignal;
  onLoaded?: (loadedBytes: number) => void;
}): Promise<string | null> {
  return new Promise((resolve, reject) => {
    if (args.signal?.aborted) {
      reject(new BrowserUploadError('UPLOAD_ABORTED'));
      return;
    }
    const xhr = new XMLHttpRequest();
    const onAbort = () => xhr.abort();
    args.signal?.addEventListener('abort', onAbort, { once: true });
    const settle = () => args.signal?.removeEventListener('abort', onAbort);
    xhr.open('PUT', putTargetUrl(args.signedUrl));
    xhr.setRequestHeader('Content-Type', args.contentType);
    xhr.setRequestHeader('Content-MD5', args.contentMd5);
    xhr.upload.onprogress = (event) => args.onLoaded?.(event.loaded);
    xhr.onload = () => {
      settle();
      if (xhr.status >= 200 && xhr.status < 300) {
        args.onLoaded?.(args.body.size);
        resolve(xhr.getResponseHeader('etag'));
      } else {
        reject(
          new BrowserUploadError('UPLOAD_PART_FAILED', `HTTP ${xhr.status}`, {
            httpStatus: xhr.status,
          })
        );
      }
    };
    xhr.onerror = () => {
      settle();
      reject(
        new BrowserUploadError('UPLOAD_PART_FAILED', 'network error', {
          noResponse: true,
        })
      );
    };
    xhr.onabort = () => {
      settle();
      reject(new BrowserUploadError('UPLOAD_ABORTED'));
    };
    xhr.send(args.body);
  });
}
